import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { Skeleton } from "@/components/ds";
import { cn } from "@/lib/cn";

// The loading shape of a list page: an optional title line, a row of filter
// controls, then table rows. From d2d-UEM ds-uem/, not in d2d-XDR ds/ (d2d-UEM plan 3.1): @datasirpi/ui had
// it and SIEM's ds/ does not. Props are the vendor's, name for name.
//
// Built from ds/Skeleton so the shimmer, colours and reduced-motion handling
// are the system's own. Rows vary in length so the placeholder reads as a
// table rather than a stack of identical bars.

export interface ListPageSkeletonProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Shown as a title line; true-ish values render a placeholder bar. */
  title?: ReactNode;
  /** Filter controls above the rows. Default 2. */
  filterCount?: number;
  /** Default 6. */
  rowCount?: number;
}

const ROW_WIDTHS = ["72%", "58%", "81%", "64%", "76%", "52%"];

export const ListPageSkeleton = forwardRef<HTMLDivElement, ListPageSkeletonProps>(function ListPageSkeleton(
  { title, filterCount = 2, rowCount = 6, className, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn("rc-listskeleton", className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      {...rest}
    >
      <span className="sr-only">Loading</span>
      {title ? <Skeleton variant="text" width="28%" height={24} /> : null}
      {filterCount > 0 ? (
        <div className="rc-listskeleton__filters" aria-hidden="true">
          {Array.from({ length: filterCount }, (_, i) => (
            <Skeleton key={i} variant="rectangle" width={i === 0 ? 280 : 160} height={36} />
          ))}
        </div>
      ) : null}
      <div className="rc-listskeleton__rows" aria-hidden="true">
        {Array.from({ length: rowCount }, (_, i) => (
          <div key={i} className="rc-listskeleton__row">
            <Skeleton variant="text" width={ROW_WIDTHS[i % ROW_WIDTHS.length]} />
            <Skeleton variant="text" width="14%" />
          </div>
        ))}
      </div>
    </div>
  );
});
